import { ApiDataProcessorType } from '@highimpact-frontend/api-types';
import { FormElement, FormElementConfigW } from './Types';

export type FieldTypes = 'string' | 'number' | 'boolean' | 'password' | 'list';

export type FieldType = {
  type: FieldTypes;
  prettyName?: string;
  defaultValue?: string | number | boolean | string[] | null;
  values?: string[];
};

type RawConfigElement = {
  defaultValue?: string | number | boolean | string[] | null;
  values?: string[];
} & Record<string, string | number | boolean | string[] | null | undefined>;

export const buildFieldFromJson = (
  configElement: RawConfigElement
): FieldType => {
  // find the first key that is not (defaultValue / values)
  const dataKey = Object.keys(configElement).find(
    (k) => k !== 'defaultValue' && k !== 'values'
  );

  if (!dataKey) {
    throw new Error('Invalid config json');
  }

  const value = configElement[dataKey];

  const type = extractFieldType(value);
  const values =
    configElement.values ??
    (Array.isArray(value) ? (value as string[]) : undefined);

  const defaultValue = configElement.defaultValue as
    | string
    | number
    | boolean
    | string[]
    | null
    | undefined;

  return {
    type,
    prettyName: dataKey,
    defaultValue,
    values,
  };
};

export const extractFieldType = (value: unknown): FieldTypes => {
  if (Array.isArray(value)) return 'list';

  switch (typeof value) {
    case 'string':
      return 'string';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
};

/** Converts field types to formelement. */
export function fieldTypeToConfig<T extends object>(
  fields: ApiDataProcessorType['configuration']
): FormElementConfigW<T>[] {
  return Object.entries(fields).map(([key, value]) => ({
    elementType: jsonTypeToConfigType(value.type),
    fields: [key as keyof T],
    transformator: undefined,
    validator: jsonTypeToValidator(key as keyof T, value.type),
    label: value.pretty_name,
    placeHolder: `Saisir ${value.pretty_name?.toLowerCase?.() || ''}`,
  })) as FormElementConfigW<T>[];
}

/** Maps JSON type to corresponding field validator(s). */
export const jsonTypeToValidator = <T extends object>(
  fieldName: keyof T,
  type: FieldTypes
): ((values: T) => string | undefined)[] => {
  switch (type) {
    case 'string':
    case 'password':
      return [
        (values: T) =>
          (values[fieldName] as string)?.length === 0
            ? 'Entrer plus d’un caractère'
            : undefined,
      ];
    case 'number':
      return [
        (values: T) => {
          return !Number.isFinite(values[fieldName] as number) &&
            values[fieldName] !== undefined
            ? 'Entrer un nombre ou laisser vide'
            : undefined;
        },
      ];
    case 'boolean':
      return [
        (values: T) =>
          (values[fieldName] as boolean) === null
            ? 'Choix obligatoire'
            : undefined,
      ];
    default:
      throw new Error(`Unsupported JSON type: ${type}`);
  }
};

/** Maps JSON type to corresponding form element. */
export const jsonTypeToConfigType = <T extends FieldTypes>(
  type: T
): FormElement => {
  switch (type) {
    case 'string':
      return 'TexInput';
    case 'password':
      return 'Password';
    case 'number':
      return 'NumberInput';
    case 'list':
      return 'DropdownItemSelector';
    case 'boolean':
      return 'Switch';
    default:
      throw new Error(`Unsupported JSON type: ${type}`);
  }
};

/** Provides default values for each JSON field type. */
export function defaultValuesFromConfig<T extends object>(
  fields: FieldType[]
): Record<keyof T, string | null | boolean> {
  return Object.entries(fields).reduce((acc, [key, value]) => {
    let defaultValue: string | null | boolean;

    switch (value.type) {
      case 'string':
      case 'password':
        defaultValue = '';
        break;
      case 'number':
        defaultValue = null;
        break;
      case 'boolean':
        defaultValue = false;
        break;
      default:
        defaultValue = null;
    }

    acc[key as keyof T] = defaultValue;
    return acc;
  }, {} as Record<keyof T, string | null | boolean>);
}
